<template>
  <!-- eslint-disable max-len -->
  <FilteredSearchLoading
    v-if="showFilteredSearchLoading"
    :searchResultsLoaded="searchResultsLoaded"
    @loadingComplete="onLoadingComplete"
  />
  <div
    v-else
    class="container search mobile-content-adjust"
    :class="{ 'search--form-active': showFormOnMobile }"
    data-page-identifier="new-search"
  >
    <div v-if="!showFormOnMobile" class="search__filters mobile-filters-adjust">
      <a href="#" class="search__filter" :class="{ 'search__filter--active': zipcode }" @click.prevent="openForm">{{
        wherePill
      }}</a>
      <a href="#" class="search__filter" :class="{ 'search__filter--active': when }" @click.prevent="openForm">{{ whenPill }}</a>
      <a href="#" class="search__filter" :class="{ 'search__filter--active': rate }" @click.prevent="openForm">{{ ratePill }}</a>
      <a href="#" class="search__filter" @click.prevent="openForm">Filters</a>
    </div>

    <div :class="{ overlay: true, 'lg:mt-32 mt-16': isBasic }">
      <Notification :message="notification" :triggerNotification="showNotification" @toggleVisibility="hideToast" />
      <aside
        ref="overlayAside"
        class="overlay__aside overlay__aside--tablet overlay__aside--right-border"
        :class="{ 'overlay__aside--active': showFormOnMobile }"
      >
        <SearchForm :accountType="accountType" @close="closeForm" />
      </aside>

      <div class="overlay__main overlay__main--tablet" :class="{ 'overlay__main--hiding': showFormOnMobile }">
        <div class="content-container">
          <header class="heading-bar-sm">
            <div class="heading-bar-main">
              <h1 class="hdg hdg--2">{{ sittersNearZipHeading }}</h1>
              <div class="heading-bar-main__sort">
                Sorted by:
                <span class="heading-bar-main__strong">Relevance</span>
                <a
                  href="https://support.sittercity.com/hc/en-us/articles/360022401334-How-do-I-find-the-sitters-that-are-most-relevant-to-me-"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                    <path
                      fill="#99005c"
                      fill-rule="nonzero"
                      d="M8 16A8 8 0 1 1 8 0a8 8 0 0 1 0 16zm0-1.371A6.629 6.629 0 1 0 8 1.37 6.629 6.629 0 0 0 8 14.63zM6.752 10.12v-.456c0-.948.636-1.536 1.212-2.1.444-.444.84-.84.84-1.356 0-.612-.336-1.008-1.068-1.008-.744 0-1.26.528-1.344 1.356H5C5.084 4.984 6.2 4 7.808 4c1.716 0 2.52.936 2.52 2.1 0 .852-.516 1.404-1.056 1.932-.564.54-1.14 1.032-1.176 1.704v.384H6.752zm.66 2.664c-.54 0-.984-.408-.984-.924 0-.528.444-.936.984-.936.528 0 .96.408.96.936 0 .516-.432.924-.96.924z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </header>

          <SearchList
            v-if="showSitters"
            :search-results="sitters"
            :caretype="queryParams.careType || 'babysitter'"
            :zip="zipcode || ''"
            :availability-checks="availabilityChecks"
            :account="account"
            :showInviteButton="shouldShowInviteButton"
            :parentJobs="parentJobs"
            :searchCareType="searchCareType"
            @scrollEnd="showMore"
            @sitterRecruited="handleSitterRecruited"
          />

          <div v-if="loading" class="search__loading heading-bar-sm">
            <p>Loading...</p>
          </div>
          <div v-else-if="noSitters" class="search-placeholder">
            <div class="heading-bar-sm">
              <h2>Sorry! No sitters are listed as available at that time</h2>
            </div>
            <p>
              But there might still be a few who are free.
              <a href="/job-post/location" class="link--primary">Post a job</a>
              to share your unique needs with local sitters, or start a search to see
              <a :href="returnToAllSearch" class="link--primary">all sitters</a>
              in your area.
            </p>
            <BHPartnersList class="mt-24" :zipCode="zipcode" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script lang="ts">
import get from 'lodash/get';
import mapKeys from 'lodash/mapKeys';
import pickBy from 'lodash/pickBy';
import snakeCase from 'lodash/snakeCase';
import { v4 as uuidv4 } from 'uuid';
import { subDays, format, isValid, formatISO } from 'date-fns';

import Notification from '@/components/notification/Notification.vue';
import SearchForm from '@/apps/search/SearchForm.vue';
import SearchList from '@/apps/search/SearchList.vue';
import BHPartnersList from '@/components/bh_partners/BHPartnersList.vue';
import { SEARCH_AVAILABILITIES_QUERY, FETCH_PARENT_OPEN_JOBS, JobEdge } from '@/graphql/search/queries';
import { parseTimeParam, validate } from '@/lib/search/form_utils';
import { sendSegmentEvent } from '../../lib/segment';
import { sendBrazeEvent } from '../../lib/braze';
import { SITTER_SEARCH_LOADED } from '@/lib/constants/braze_custom_events';
import { AVAILABILITY_CHECKS } from '@/graphql/availability/queries';
import { isOn } from '@/lib/growthbook';
import SearchResponseHandler from './helpers/searchResponseHandler';
import FilteredSearchLoading from './FilteredSearchLoading.vue';
import { allowedJobCareTypesForSearchCareType } from '@/helpers/allowedJobCareTypes';

declare let google: any;

export default {
  name: 'Search',
  components: {
    SearchList,
    SearchForm,
    Notification,
    BHPartnersList,
    FilteredSearchLoading,
  },
  data(): any {
    return {
      notification: null,
      loading: false,
      showFormOnMobile: false,
      showNotification: false,
      searchParams: null,
      searchResults: null,
      sitters: null,
      availabilityChecks: null,
      handlePageShow: null,
      parentJobs: [] as JobEdge[],
      searchResultsLoaded: false,
      showFilteredSearchLoading: false,
      isFromJobPost: false,
    };
  },
  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    totalSitterCount(): number {
      return this.searchResults?.totalCount || 0;
    },
    accountType(): boolean {
      return this.account?.accountType();
    },
    isParent(): boolean {
      return this.account?.isParent() || false;
    },
    searchCareType(): string {
      const rawCareType = this.$route.query?.careType;
      const normalizedCareType = rawCareType ? rawCareType.toLowerCase() : '';
      if (normalizedCareType === 'both' || normalizedCareType === 'childcare') {
        return 'childcare';
      }
      if (normalizedCareType === 'babysitter' || normalizedCareType === 'nanny') {
        return normalizedCareType;
      }
      return 'babysitter';
    },
    allowedJobCareTypes(): string[] {
      return allowedJobCareTypesForSearchCareType(this.searchCareType);
    },
    hasMatchingCareTypeJobs(): boolean {
      return this.parentJobs.some((edge: JobEdge) => edge.node.careType && this.allowedJobCareTypes.includes(edge.node.careType));
    },
    shouldShowInviteButton(): boolean {
      if (!this.isParent) {
        return false;
      }
      if (!this.hasMatchingCareTypeJobs) {
        return false;
      }

      return isOn('sc-invite-to-apply-sitter-search');
    },
    careTypeHeaders(): 'nannies' | 'babysitters' | 'sitters' {
      const { careType } = this.$route.query;

      if (careType === 'nanny') {
        return 'nannies';
      }
      if (careType === 'babysitter') {
        return 'babysitters';
      }

      return 'sitters';
    },
    sittersNearZipHeading(): string {
      if (!this.searchResults) {
        return `${this.headingCareType} near ${this.zipcode || 'you'}`;
      }
      const totalSitterCount: number | string = this.totalSitterCount < 1000 ? this.totalSitterCount : '1000+';
      return `${totalSitterCount} ${this.careTypeHeaders} near ${this.zipcode || 'you'}`;
    },
    isBasic(): boolean {
      return this.accountType === 'basic';
    },
    headingCareType(): string {
      const { careType } = this.$route.query;

      switch (careType) {
        case 'nanny':
          return 'Nannies';
        case 'babysitter':
          return 'Babysitters';
        default:
          return 'Childcare';
      }
    },
    hasMore(): boolean {
      return get(this.searchResults, 'pageInfo.hasNextPage');
    },
    hasSitters(): boolean {
      return this.sitters && this.sitters.length > 0;
    },
    noSitters(): boolean {
      return this.sitters && this.sitters.length === 0;
    },
    lastAvailability(): string | null {
      if (this.hasSitters) {
        return this.sitters[this.sitters.length - 1].cursor;
      }
      return null;
    },
    canLoadMore(): boolean {
      return this.hasMore && !this.loading;
    },
    showSitters(): boolean {
      return !!this.sitters;
    },
    returnToAllSearch(): string {
      const searchRedirect = '/availability/search';
      if (this.zipcode) {
        return `${searchRedirect}?zipcode=${this.zipcode}`;
      }
      return searchRedirect;
    },
    zipcode(): string | null {
      if (!this.valid) return null;
      return get(this.queryParams, 'zipcode', null);
    },
    wherePill(): string {
      if (this.zipcode) return this.zipcode;
      return 'Where';
    },
    rate(): number | null {
      if (!this.valid) return null;
      return get(this.queryParams, 'rate', null);
    },
    ratePill(): string {
      if (this.rate) return `Max $${this.rate}`;
      return 'Rate';
    },
    when(): Date | null {
      if (!this.valid) return null;
      return get(this.queryParams, 'start', null);
    },
    whenPill(): string {
      if (this.when && isValid(this.when)) return format(this.when, 'MMM d');
      return 'When';
    },
    queryParams(): any {
      if (!this.$route.query) return {};

      const params: any = {
        zipcode: this.$route.query.zipcode,
        sid: this.$route.query.sid || uuidv4(),
        keywords: this.$route.query.keywords,
      };

      const {
        start,
        end,
        distance,
        hasCar,
        rate,
        yearsExperience,
        careType,
        providesSchoolSupport,
        languages,
        lastLoginDays,
        backgroundChecks,
      } = this.$route.query;

      if (start) params.start = parseTimeParam(start);
      if (end) params.end = parseTimeParam(end);

      const parsedDistance = parseInt(distance, 10);
      if (parsedDistance > 0) params.distance = parsedDistance;

      if (hasCar) params.hasCar = hasCar === 'true';
      if (providesSchoolSupport) {
        params.providesSchoolSupport = providesSchoolSupport === 'true';
      }

      // DB value is stored in the hundredths
      const parsedRate = parseInt(rate, 10);
      // specifically allow sending $0 rate to the search api
      if (parsedRate >= 0) params.rate = parsedRate;

      const parsedYearsExperience = parseInt(yearsExperience, 10);
      if (parsedYearsExperience) params.yearsExperience = parsedYearsExperience;

      if (careType === 'both') {
        params.careType = null;
      } else {
        params.careType = careType;
      }

      if (!!languages && languages.length !== 0) {
        if (Array.isArray(languages)) {
          params.languages = languages;
        } else {
          params.languages = Array.of(languages);
        }
      }

      const d = subDays(new Date(), lastLoginDays || 42);
      params.lastLoginAt = format(d, 'yyyy-MM-dd');

      if (!!backgroundChecks && backgroundChecks.length !== 0) {
        if (Array.isArray(backgroundChecks)) {
          params.backgroundChecks = backgroundChecks;
        } else {
          params.backgroundChecks = Array.of(backgroundChecks);
        }
      }

      return pickBy(params, (p) => !!p || p === 0);
    },
    valid(): boolean {
      const errors = validate(this.queryParams);
      return Object.keys(errors).length === 0;
    },
  },
  watch: {
    // eslint-disable-next-line object-shorthand, func-names
    $route: function () {
      this.submitSearch();
    },
  },
  beforeMount() {
    this.handlePageShow = (event) => {
      if (event.persisted) {
        // safari has a quirk where it doesn't reload the page on back navigation and instead loads cached exit state, this forces reload
        // this was breaking filters when going from childcare to special needs
        window.location.reload();
      }
    };
    window.onpageshow = this.handlePageShow;
  },
  async created() {
    if (!this.account) {
      await this.$store.dispatch('account/fetchAccountData');
    }
    // Call fetchParentJobs after account is loaded (await above ensures account is ready)
    this.fetchParentJobs();
  },
  mounted() {
    this.initFilteredSearchLoading();

    sendBrazeEvent(SITTER_SEARCH_LOADED);
    // turn off buggy chrome scroll scrollRestoration
    // this self restores when you navigate somewhere else
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    this.submitSearch();
    this.checkRecruits();
  },
  beforeDestroy() {
    // Clean up window event listener
    if (this.handlePageShow) {
      window.onpageshow = null;
    }
  },
  methods: {
    async submitSearch(): Promise<any> {
      if (!this.valid) return null;

      if (!this.account) {
        await this.$store.dispatch('account/fetchAccountData');
      }

      sendSegmentEvent('Sitter Search Requested', this.sitterSearchRequestedParams(this.queryParams));
      this.showFormOnMobile = false;

      if (this.zipcode) {
        return this.geoCode(this.queryParams.zipcode)
          .then((data) => {
            this.runSearch(this.buildSearchParams(data[0].geometry.location.lng(), data[0].geometry.location.lat()));
          })
          .catch(() => {
            this.loading = false;
          });
      }

      return this.submitDefaultSearch();
    },
    submitDefaultSearch() {
      if (!this.valid) return null;

      if (!this.account || !this.account.location) {
        console.error('Account or location data not available for default search');
        return null;
      }

      return this.runSearch(this.buildSearchParams(this.account.location.longitude, this.account.location.latitude));
    },
    geoCode(zipcode: string): any {
      return new Promise<any>((resolve, reject) => {
        const componentRestrictions = { country: 'US', postalCode: zipcode };
        new google.maps.Geocoder().geocode({ componentRestrictions }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve(results);
          } else {
            console.error('Geocode was not successful', status, results);
            reject(new Error(`Invalid zipcode: ${zipcode}`));
          }
        });
      });
    },
    sitterSearchRequestedParams(segParams: any): any {
      const { sid, zipcode, start, end, rate, ...rest } = segParams;
      const params: any = {
        ...rest,
        search_uuid: sid,
      };

      if (zipcode) params.postal_code = zipcode;
      if (start) params.start_time = formatISO(start);
      if (end) params.end_time = formatISO(end);

      if (rate >= 0) params.rate = rate * 100;

      return mapKeys(params, (value, key) => snakeCase(key));
    },
    // TODO: when GraphQL work is completed, we should change locationX and locationY to numbers)
    buildSearchParams(locationX: any, locationY: any): any {
      const { sid, zipcode, start, end, rate, careType, keywords, ...rest } = this.queryParams;

      const params: any = {
        ...rest,
        geoPoint: {
          x: locationX.toString(),
          y: locationY.toString(),
        },
      };

      if (start) params.start = formatISO(start);
      if (end) params.end = formatISO(end);

      const defaultHourlyRate = 20;
      if (rate >= 0) {
        params.rate = rate * 100;
      } else {
        params.rate = defaultHourlyRate * 100;
      }

      if (careType) params.careType = careType;

      if (keywords) params.keywords = keywords;

      return params;
    },
    runSearch(params: any) {
      // clear previous results
      this.searchResults = null;
      this.sitters = null;

      // set up for new results
      this.loading = true;
      this.searchParams = params;
      this.scrollToTop();

      this.$apollo
        .query({
          query: SEARCH_AVAILABILITIES_QUERY,
          errorPolicy: 'all',
          variables: { first: 10, searchParams: params },
        })
        .then((payload) => {
          SearchResponseHandler.updateSearchResults(this, payload, false);
          this.searchResultsLoaded = true;
        })
        .catch(this.onError);
    },
    checkRecruits() {
      this.$apollo
        .query({
          query: AVAILABILITY_CHECKS,
        })
        .then(({ data }) => {
          this.availabilityChecks = data?.availabilityChecks || [];
        })
        // eslint-disable-next-line no-unused-vars
        .catch((error) => {
          this.availabilityChecks = [];
        });
    },
    handleSitterRecruited(sitterUuid: string | null) {
      if (!sitterUuid) {
        return;
      }
      // Add the sitter to the local availabilityChecks array to update the UI immediately
      const checks = this.availabilityChecks || [];
      if (!checks.includes(sitterUuid)) {
        this.availabilityChecks = [...checks, sitterUuid];
      }
    },
    fetchParentJobs(): void {
      this.$apollo
        .query({
          query: FETCH_PARENT_OPEN_JOBS,
          variables: {
            expiresAfter: new Date().toISOString(),
            careTypes: this.allowedJobCareTypes,
          },
        })
        .then(({ data }) => {
          this.parentJobs = data?.viewer?.jobs?.edges || [];
        })
        .catch(() => {
          this.parentJobs = [];
        });
    },

    searchResultReceived(payload) {
      const e = get(payload, 'data.sitterSearch.edges');
      if (e && e.length > 0) {
        e.forEach((edge) => {
          sendSegmentEvent('Sitter Search Returned', {
            search_uuid: this.queryParams.sid,
            sitter_uuid: get(edge, 'node.sitter.id'),
            has_photo: !!get(edge, 'node.sitter.photoUrl'),
            average_rating: get(edge, 'node.avgRating'),
            distance: get(edge, 'node.distance'),
          });
        });
      }
    },
    onError(error) {
      console.error(error); // eslint-disable-line no-console
      this.notify("Oops! We can't process this search right now. Try clicking the 'All Sitters' tab to browse local sitters.");
      this.loading = false;
    },
    showMore() {
      if (!this.canLoadMore) return;

      this.loading = true;
      this.$apollo
        .query({
          query: SEARCH_AVAILABILITIES_QUERY,
          errorPolicy: 'all',
          variables: {
            first: 10,
            after: this.lastAvailability,
            searchParams: this.searchParams,
          },
        })
        .then((payload) => {
          SearchResponseHandler.updateSearchResults(this, payload, true);
        })
        .catch(this.onError);
    },
    notify(message: string) {
      this.notification = message;
      this.showNotification = true;
    },
    hideToast() {
      this.showNotification = false;
    },
    openForm() {
      this.showFormOnMobile = true;
      this.scrollToTop();
    },
    closeForm() {
      this.showFormOnMobile = false;
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    initFilteredSearchLoading() {
      this.isFromJobPost = this.$route.query.source === 'job-post';
      this.showFilteredSearchLoading = this.isFromJobPost;

      // Remove source query param to prevent showing loading again on refresh
      if (this.isFromJobPost) {
        const { source, ...query } = this.$route.query;
        this.$router.replace({ query });
      }
    },
    onLoadingComplete() {
      this.showFilteredSearchLoading = false;
    },
  },
};
</script>
